import { useEffect, useState, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import {
  Menu,
  Search,
  X,
  Gamepad2,
  Home,
  Compass,
  Layers,
  LayoutGrid,
  ArrowRight,
  ShoppingBag,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import logoAsset from "@/assets/optimized/logo-64.webp.asset.json";
import { SurpriseButton } from "@/components/SurpriseButton";

import { resolveImage, sizedImage } from "@/lib/asset-map";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { RankChip } from "@/components/RankChip";
import { Button } from "@/components/ui/button";
import { SHOP_ENABLED } from "@/lib/feature-flags";

// Search + account pull in heavy code (dialog, auth client). Keep them out of
// the first-paint bundle; they only matter after the visitor interacts.
const SearchDialog = lazy(() =>
  import("@/components/SearchDialog").then((m) => ({ default: m.SearchDialog })),
);
const AccountMenu = lazy(() =>
  import("@/components/AccountMenu").then((m) => ({ default: m.AccountMenu })),
);

type NavItem = {
  label: string;
  to:
    | "/"
    | "/play"
    | "/discoveries"
    | "/collections"
    | "/categories"
    | "/shop";
  icon: typeof Home;
};

// One clear visitor path. Distinct tools are promoted contextually instead
// of competing with the core discovery and play destinations in every menu.
const PRIMARY: NavItem[] = [
  { label: "Home", to: "/", icon: Home },
  { label: "Articles", to: "/discoveries", icon: Compass },
  { label: "Series", to: "/collections", icon: Layers },
  { label: "Categories", to: "/categories", icon: LayoutGrid },
  { label: "Play", to: "/play", icon: Gamepad2 },
  ...(SHOP_ENABLED ? [{ label: "Shop", to: "/shop", icon: ShoppingBag } as NavItem] : []),
];


// Stable category list (matches DB). Keep here to avoid an extra fetch in the header.
// Slugs MUST match the `categories` table in the database. Keep in sync.
const CATEGORIES: { slug: string; label: string; img: string }[] = [
  { slug: "animals", label: "Wild Things", img: "cat-animals.jpg" },
  { slug: "ocean", label: "Deep Blue", img: "cat-ocean.jpg" },
  { slug: "space", label: "Out There", img: "cat-space.jpg" },
  { slug: "body", label: "You, Inside", img: "cat-body.jpg" },
  { slug: "earth-nature", label: "Our Weird Planet", img: "coll-danakil-depression.jpg" },
  { slug: "ancient-earth", label: "Long Ago", img: "ae1.jpg" },
  { slug: "physics-chemistry", label: "How Stuff Works", img: "cat-facts.jpg" },
  { slug: "mind-psychology", label: "Head Stuff", img: "thumb-brain-greedy.jpg" },
  { slug: "survival-disasters", label: "Danger Zone", img: "cat-nature.jpg" },
  { slug: "technology", label: "Tech Weirdness", img: "pw1.jpg" },
];

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // The account menu drags in the auth client (~54 KB) and blocks the main
  // thread during first paint. Mount it once the page is idle instead.
  const [accountReady, setAccountReady] = useState(false);
  useEffect(() => {
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    };
    if (w.requestIdleCallback) {
      w.requestIdleCallback(() => setAccountReady(true), { timeout: 3000 });
      return;
    }
    const t = window.setTimeout(() => setAccountReady(true), 2000);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    // Lightweight scroll lock - overflow only, no position:fixed.
    // Safer across mobile browsers; can't strand the page in an unscrollable state.
    const body = document.body;
    const prevOverflow = body.style.overflow;
    body.style.overflow = "hidden";

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPop = () => setOpen(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("popstate", onPop);
    return () => {
      body.style.overflow = prevOverflow;
      // Defensive: clear any stale lock styles that may have been applied
      // by previous versions of this effect.
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("popstate", onPop);
    };
  }, [open]);

  // One-time safety: clear any leftover scroll-lock styles on mount.
  useEffect(() => {
    const body = document.body;
    if (body.style.position === "fixed") {
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      body.style.overflow = "";
    }
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable))
        return;
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const closeMenu = () => setOpen(false);
  return (
    <header
      className={`sticky top-0 z-[100] transition-all duration-300 ${
        scrolled
          ? "border-b border-border bg-background/90 backdrop-blur-xl"
          : "bg-background/70 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none"
      }`}
    >
      <div className="mx-auto hidden h-16 max-w-7xl grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-8 md:grid">
        <Link to="/" className="flex min-w-0 items-center gap-2" onClick={closeMenu}>
          <img
            src={logoAsset.url}
            alt=""
            width={32}
            height={32}
            className="h-7 w-7 shrink-0 object-contain md:h-8 md:w-8"
          />
          <span
            className="notranslate truncate font-display text-xl tracking-wide"
            translate="no"
          >
            SMARTASAURUS
          </span>
        </Link>

        <div className="flex shrink-0 items-center gap-3 md:gap-5">
          <nav className="hidden items-center gap-5 lg:flex">
            {PRIMARY.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="inline-flex min-h-11 items-center text-xs font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
                activeProps={{ className: "text-foreground" }}
                activeOptions={{ exact: item.to === "/" }}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-0.5 md:gap-2">
            {/* Rank stays visible on every screen - it's the progress payoff. */}
            <RankChip />
            <div className="hidden items-center sm:flex">
              <SurpriseButton />
            </div>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Search"
              aria-keyshortcuts="/"
              onClick={() => setSearchOpen(true)}
              className="h-11 w-11 rounded-full text-muted-foreground hover:text-foreground"
            >
              <Search className="h-4 w-4" />
            </Button>
            <LanguageSwitcher compact />
            {accountReady ? (
              <Suspense fallback={<div className="h-11 w-11" aria-hidden />}>
                <AccountMenu />
              </Suspense>
            ) : (
              <div className="h-11 w-11" aria-hidden />
            )}

            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="h-11 w-11 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Phone header: centered brand first, utilities on their own calm row. */}
      <div className="md:hidden">
        <div className="relative grid h-14 grid-cols-[44px_minmax(0,1fr)_44px] items-center border-b border-border/70 px-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="h-11 w-11 rounded-full text-muted-foreground hover:text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>

          <Link
            to="/"
            className="notranslate mx-auto flex min-w-0 max-w-full items-center justify-center gap-1.5 px-1"
            onClick={closeMenu}
            translate="no"
            aria-label="Smartasaurus home"
          >
            <img src={logoAsset.url} alt="" width={28} height={28} className="h-7 w-7 shrink-0 object-contain" />
            <span className="whitespace-nowrap font-display text-[clamp(0.86rem,4vw,1.05rem)] tracking-wide">
              SMARTASAURUS
            </span>
          </Link>

          <div className="flex h-11 w-11 items-center justify-center">
            {accountReady ? (
              <Suspense fallback={<div className="h-11 w-11" aria-hidden />}>
                <AccountMenu />
              </Suspense>
            ) : (
              <div className="h-11 w-11" aria-hidden />
            )}
          </div>
        </div>

        <div className="flex h-12 items-center justify-between gap-2 px-3">
          <RankChip />
          <div className="ml-auto flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Search"
              aria-keyshortcuts="/"
              onClick={() => setSearchOpen(true)}
              className="h-11 w-11 rounded-full text-muted-foreground hover:text-foreground"
            >
              <Search className="h-4 w-4" />
            </Button>
            <LanguageSwitcher compact />
          </div>
        </div>
      </div>

      {/* Streaming-style full-screen menu */}
      {open &&
        mounted &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            className="fixed inset-0 top-[6.5rem] z-[90] overflow-y-auto overflow-x-hidden bg-background/70 pb-safe backdrop-blur-2xl md:top-16"
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <div className="mx-auto max-w-6xl px-4 py-6 md:px-8 md:py-10">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  Menu
                </span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeMenu}
                  aria-label="Close menu"
                  className="h-9 rounded-full px-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground hover:border-primary/50 hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" /> Close
                </Button>
              </div>
              {/* Widgets hidden from the compact mobile header live here */}
              <div className="mb-4 flex items-center gap-2 sm:hidden">
                <SurpriseButton />
              </div>

              {/* Primary destinations */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                {PRIMARY.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={closeMenu}
                      className="group flex flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-card p-4 text-center transition hover:border-primary/50"
                      activeOptions={{ exact: item.to === "/" }}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-[11px] font-bold uppercase tracking-widest">
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </div>

              {/* Categories - visual */}
              <div className="mt-8">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                    Browse Categories
                  </h3>
                  <Link
                    to="/categories"
                    onClick={closeMenu}
                    className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-primary"
                  >
                    All <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {CATEGORIES.map((c) => (
                    <Link
                      key={c.slug}
                      to="/category/$slug"
                      params={{ slug: c.slug }}
                      onClick={closeMenu}
                      className="group relative aspect-[16/10] overflow-hidden rounded-xl border border-border bg-card"
                    >
                      <img
                        src={sizedImage(c.img, 330)}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-110 group-hover:opacity-90"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-3">
                        <div className="text-sm font-bold leading-tight text-white">{c.label}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

            </div>
          </div>,
          document.body,
        )}
      {searchOpen && (
        <Suspense fallback={null}>
          <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
        </Suspense>
      )}
    </header>
  );
}
